/**
 * 檔案處理輔助函數
 */

/**
 * 將檔案轉換為 Base64 格式
 * @param {File} file 檔案物件
 * @returns {Promise<string>} Base64 編碼字串
 */
export function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = error => reject(error);
  });
}

/**
 * 驗證檔案大小
 * @param {File} file 檔案物件
 * @param {number} maxSizeMB 最大檔案大小（MB）
 * @returns {boolean} 是否通過驗證
 */
export function validateFileSize(file, maxSizeMB = 10) {
  const maxSizeBytes = maxSizeMB * 1024 * 1024;
  return file.size <= maxSizeBytes;
}

/**
 * 驗證檔案類型
 * @param {File} file 檔案物件
 * @param {Array<string>} allowedTypes 允許的檔案類型（MIME type 或副檔名）
 * @returns {boolean} 是否通過驗證
 */
export function validateFileType(file, allowedTypes = []) {
  if (!allowedTypes || allowedTypes.length === 0) {
    return true;
  }
  
  // 檢查 MIME type
  const mimeTypeMatch = allowedTypes.some(type => {
    if (type.includes('/')) {
      return file.type === type;
    }
    return false;
  });
  
  if (mimeTypeMatch) {
    return true;
  }
  
  // 檢查副檔名
  const fileName = file.name.toLowerCase();
  const extensionMatch = allowedTypes.some(type => {
    if (!type.includes('/')) {
      const ext = type.startsWith('.') ? type : `.${type}`;
      return fileName.endsWith(ext.toLowerCase());
    }
    return false;
  });
  
  return extensionMatch;
}

/**
 * 格式化檔案大小顯示
 * @param {number} bytes 檔案大小（bytes）
 * @returns {string} 格式化後的字串
 */
export function formatFileSize(bytes) {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
}

/**
 * 取得檔案副檔名
 * @param {string} fileName 檔案名稱
 * @returns {string} 副檔名（包含 .）
 */
export function getFileExtension(fileName) {
  const lastDotIndex = fileName.lastIndexOf('.');
  return lastDotIndex !== -1 ? fileName.substring(lastDotIndex) : '';
}

/**
 * 驗證檔案
 * @param {File} file 檔案物件
 * @param {Object} options 驗證選項
 * @param {number} options.maxSizeMB 最大檔案大小（MB）
 * @param {Array<string>} options.allowedTypes 允許的檔案類型
 * @returns {Object} 驗證結果 { valid: boolean, message: string }
 */
export function validateFile(file, options = {}) {
  const { maxSizeMB = 10, allowedTypes = [] } = options;
  
  if (!file) {
    return { valid: false, message: '請選擇檔案' };
  }
  
  // 檢查檔案大小
  if (!validateFileSize(file, maxSizeMB)) {
    return { 
      valid: false, 
      message: `檔案大小不能超過 ${maxSizeMB}MB（目前：${formatFileSize(file.size)}）` 
    };
  }
  
  // 檢查檔案類型
  if (allowedTypes.length > 0 && !validateFileType(file, allowedTypes)) {
    return { 
      valid: false, 
      message: `不支援的檔案格式。允許的格式：${allowedTypes.join(', ')}` 
    };
  }
  
  return { valid: true, message: '驗證通過' };
}

export default {
  fileToBase64,
  validateFileSize,
  validateFileType,
  formatFileSize,
  getFileExtension,
  validateFile
};
